import { useEffect, useRef, type ReactNode, type Dispatch, type SetStateAction } from 'react'
import { motion } from 'framer-motion'

import AuraCard from '../aura-card'
import ChatBubble from './chat-bubble'
import { useAskAiChat } from './use-ask-ai-chat'
import Typewriter from './typewriter'
import { isAlgoliaAiConfigValid } from './api'
import useAskAiSettings from './use-ask-ai-settings'

import AiIcon from '@/icons/ai-icon.svg'
import ArrowIcon from '@/icons/bwi/arrow-right.svg'
import CloseIcon from '@/icons/bwi/close.svg'
import RefreshIcon from '@/icons/bwi/refresh.svg'

import { cx } from '@/utils/strings'
import { useTranslations } from '@/utils/locale'
import StaggerList, { StaggerListItem } from '../ui/stagger-list'
import { useSearch } from '../search-provider'
import { useLocation } from '@/utils/url'
import { getModKey } from '@/utils/user-info'

const INPUT_MAX_HEIGHT = 160

export type WideAskAiVariant = 'shadow' | 'glow'

export type WideAskAiProps = {
  variant?: WideAskAiVariant
  agentId?: string
  className?: string
  isOpen: boolean
  setIsOpen: Dispatch<SetStateAction<boolean>>
}

function AskAiShell({
  variant,
  rounded,
  children,
}: {
  variant: WideAskAiVariant
  rounded: 'full' | '3xl'
  children: ReactNode
}) {
  const shellClassName = cx('w-full mx-auto bg-secondary-900', rounded === '3xl' ? 'rounded-lg' : 'rounded-2xl')
  if (variant === 'glow') {
    return (
      <AuraCard className={cx(shellClassName)} colors={['#76FF7A', '#38F0C2']} speed="5s" glowSize="sm">
        {children}
      </AuraCard>
    )
  }
  return (
    <div className={cx(shellClassName, 'border border-secondary-100 dark:border-secondary-700 shadow-2xl')}>
      {children}
    </div>
  )
}

/**
 * Hero-embeddable Ask AI input. A separate, self-contained surface from the
 * floating bottom-right widget (`FloatingChat`) — it runs its own chat
 * instance rather than the shared `useSearch()` trigger, so it can be
 * mounted on the same page as (or in place of) the floating widget without
 * both firing on the same query. See `use-ask-ai-chat.ts` for the shared
 * Algolia plumbing both surfaces build on.
 *
 * `style`, `showCta` and `promptSet` are plain props for now — wiring real
 * per-variant Ninetailed/Contentful switching (one independent experience
 * per dimension) is a follow-up once the content-modeling approach for
 * `componentCard`/`sectionSetOfCards` is confirmed.
 */
export default function WideAskAi({ variant = 'shadow', agentId, className, isOpen, setIsOpen }: WideAskAiProps) {
  const { t } = useTranslations()
  const { messages, status, isLoading, error, transportError, sendPrompt, stop, handleFeedback, reset } = useAskAiChat(
    agentId,
    { ui: 'wide' }
  )
  const { promptSuggestions = [], placeholders = [] } = useAskAiSettings() || {}
  const { askAiAgent, aiQuery, setAiQuery, trackChatOpen } = useSearch()
  const { pathWithoutLocale } = useLocation()

  const inputRef = useRef<HTMLTextAreaElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const shouldAutoScroll = useRef(true)
  const scrollToBottom = () => {
    if (scrollContainerRef.current) {
      const { scrollHeight, clientHeight } = scrollContainerRef.current
      scrollContainerRef.current.scrollTop = scrollHeight - clientHeight
    }
  }

  // Handle sending AI query from provider (e.g., the shared `useSearch()` hook)
  useEffect(() => {
    if (aiQuery) {
      sendPrompt(aiQuery).then(() => {
        setAiQuery('') // Clear after message is processed
      })
    }
  }, [aiQuery])

  // Auto-scroll when messages update, during streaming, and once more when
  // streaming finishes (the typing indicator is swapped for feedback/copy
  // buttons at that point, which shifts the content height)
  useEffect(() => {
    if (shouldAutoScroll.current) {
      scrollToBottom()
    }
  }, [messages, status])

  // Refocus the input once a response finishes streaming so the user can keep typing
  useEffect(() => {
    if (isOpen && !isLoading) {
      inputRef.current?.focus()
    }
  }, [isOpen, isLoading])

  // Close on escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  // Close when clicking outside the component
  useEffect(() => {
    if (!isOpen) return

    const handlePointerDown = (e: PointerEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('pointerdown', handlePointerDown)
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
    }
  }, [isOpen])

  if (!isAlgoliaAiConfigValid()) return null

  const handleOpen = () => {
    setIsOpen(true)
    setTimeout(() => inputRef.current?.focus(), 10)

    // Track chat open event
    if (typeof window !== 'undefined') {
      // Track with GTM dataLayer
      if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push({
          event: 'chat_opened',
          chatSource: 'manual',
          agent: askAiAgent,
          pagePath: pathWithoutLocale,
          ui: 'wide',
        })
      }
      trackChatOpen('wide', { pagePath: pathWithoutLocale })
    }
  }

  const submit = (value: string) => {
    if (!value.trim()) return
    shouldAutoScroll.current = true
    sendPrompt(value)
    if (inputRef.current) {
      inputRef.current.value = ''
      inputRef.current.style.height = 'auto'
    }
  }

  const resizeInput = () => {
    const el = inputRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, INPUT_MAX_HEIGHT)}px`
  }

  return (
    <motion.div
      ref={containerRef}
      className={cx('max-w-[calc(100vw-2rem)] mx-auto flex flex-col items-start gap-2 origin-bottom', className)}
      style={{
        originX: 0.5,
        originY: 1,
      }}
      initial="closed"
      animate={isOpen ? 'open' : 'closed'}
      variants={{
        open: { width: 500 },
        closed: { width: 340 },
      }}
      transition={{ type: 'spring', duration: 0.3, bounce: 0.2, origin: 'bottom' }}
    >
      {isOpen && messages.length === 0 && (
        <div className="flex flex-col items-start gap-2 px-1 relative z-1 transition-discrete">
          <StaggerList isVisible reverseDirection className="flex flex-col gap-2 list-none">
            {promptSuggestions.map((item) => (
              <StaggerListItem key={item.button}>
                <button
                  type="button"
                  onClick={() => {
                    sendPrompt(item.prompt)
                    if (typeof window.plausible === 'function') {
                      window.plausible('AI Chat Starter Suggestion Clicked', {
                        props: {
                          agent: askAiAgent,
                          pagePath: pathWithoutLocale,
                          ui: 'wide',
                          suggestion: item.prompt,
                        },
                      })
                    }
                  }}
                  className={cx(
                    'bg-secondary-900/90 dark:bg-white/70 backdrop-blur-3xl text-sm font-medium text-white dark:text-black',
                    'rounded-full px-4 py-1.5 [background-clip:padding-box] transform-gpu isolate border border-white/10 dark:border-black/10',
                    'hover:bg-secondary-900 dark:hover:bg-white transition-colors duration-200'
                  )}
                >
                  {t(item.button)}
                </button>
              </StaggerListItem>
            ))}
          </StaggerList>
        </div>
      )}
      {isOpen && messages.length > 0 && (
        <div className="w-[500px] max-w-full animate-fade-in-up">
          <div
            className={cx(
              'bg-secondary-900 firefox:bg-secondary-900/90 firefox:backdrop-blur-3xl rounded-lg',
              'border border-white/20'
            )}
          >
            <div className="flex flex-col gap-3 px-1.5 md:px-3 py-3">
              <div className="flex items-center justify-between px-1">
                <span className="flex items-center gap-2 text-sm font-medium text-white">
                  <AiIcon aria-hidden="true" className="text-primaryBlue size-4" />
                  {t('Ask Bitwarden')}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={reset}
                    aria-label={t('Reset Chat')}
                    className="text-secondary-400 hover:text-secondary-600 dark:hover:text-secondary-200"
                  >
                    <RefreshIcon className="size-4" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsOpen(false)}
                    aria-label={t('Close')}
                    className="text-secondary-400 hover:text-secondary-600 dark:hover:text-secondary-200"
                  >
                    <CloseIcon className="size-4" aria-hidden="true" />
                  </button>
                </div>
              </div>

              <div
                ref={scrollContainerRef}
                className="flex flex-col gap-3 h-[500px] max-h-[80vh] overflow-y-auto px-3 scrollbar-thumb-secondary-200 scrollbar-thin"
                onScroll={() => {
                  if (scrollContainerRef.current) {
                    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current
                    const atBottom = scrollTop + clientHeight >= scrollHeight - 10 // 10px threshold

                    // Enable auto-scroll if user scrolls to bottom, disable if they scroll away
                    shouldAutoScroll.current = atBottom
                  }
                }}
              >
                {messages.map((item) =>
                  item.role === 'user' ? (
                    <div key={item.id} className="text-right">
                      <ChatBubble {...item} state="complete" size="sm" animated={false} />
                    </div>
                  ) : (
                    <div key={item.id}>
                      <ChatBubble
                        {...item}
                        state={
                          status === 'streaming' && item.id === messages[messages.length - 1].id
                            ? 'streaming'
                            : 'complete'
                        }
                        variant="text-white"
                        size="sm"
                        animated={false}
                        onFeedback={(feedback) => handleFeedback(item.id, feedback)}
                        onSuggestionClick={sendPrompt}
                        onLeadFormSubmit={() => sendPrompt(t('Form submitted'))}
                        onStreamUpdate={() => {
                          if (shouldAutoScroll.current) {
                            scrollToBottom()
                          }
                        }}
                      />
                    </div>
                  )
                )}
                {(transportError || error) && (
                  <ChatBubble
                    id="hero-ask-ai-error"
                    role="assistant"
                    state="error"
                    parts={[
                      {
                        type: 'text',
                        text:
                          transportError ||
                          error?.message ||
                          t('An unexpected error occurred. Please try again later.'),
                      },
                    ]}
                    animated={false}
                    size="sm"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <AskAiShell variant={variant} rounded="full">
        {!isOpen ? (
          <button
            type="button"
            onClick={handleOpen}
            aria-label={`${t('Ask AI')} (${getModKey()} + I)`}
            title={`${t('Ask AI')} (${getModKey()} + I)`}
            className="flex items-center gap-2.5 w-full h-11 pl-4 pr-2.5 text-left animate-fade-in"
          >
            <AiIcon aria-hidden="true" className="text-primaryBlue size-5 shrink-0" />
            <span className="relative flex-1 min-w-0 h-5 overflow-hidden">
              <Typewriter
                phrases={placeholders}
                className="absolute inset-0 flex items-center text-sm text-secondary-200 whitespace-nowrap overflow-hidden text-ellipsis"
              />
            </span>
            <span className="flex items-center justify-center size-7 rounded-full shrink-0 bg-white text-black">
              <ArrowIcon className="size-4.5 mx-auto translate-x-px" aria-hidden="true" />
            </span>
          </button>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault()
              submit(inputRef.current?.value || '')
            }}
            className="flex items-center gap-2 min-h-11 pl-4 pr-2.5"
          >
            {messages.length === 0 && (
              <Typewriter
                phrases={placeholders}
                className="hidden"
                onTextChange={(text) => {
                  if (inputRef.current) inputRef.current.placeholder = text
                }}
              />
            )}
            <textarea
              ref={inputRef}
              aria-label={t('Ask a question')}
              disabled={isLoading}
              autoFocus
              rows={1}
              onFocus={() => {
                if (inputRef.current) inputRef.current.placeholder = ''
              }}
              onInput={resizeInput}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  submit(inputRef.current?.value || '')
                }
              }}
              autoComplete="off"
              className="flex-1 min-w-0 resize-none py-2.5 max-h-40 overflow-y-auto bg-transparent text-sm text-white placeholder:text-secondary-400 focus:outline-none disabled:text-secondary-400 scrollbar-thumb-secondary-200 scrollbar-thin"
            />
            {!isLoading ? (
              <button
                type="submit"
                aria-label={t('Send message')}
                title={t('Send message')}
                className={cx(
                  'flex items-center justify-center size-8 rounded-full shrink-0 bg-primaryBlue dark:bg-white text-white dark:text-black disabled:opacity-50',
                  'transition-[background-color,opacity] duration-200 ease-in-out hover:bg-black dark:hover:bg-primary-300'
                )}
              >
                <ArrowIcon className="size-4 mx-auto translate-x-px" aria-hidden="true" />
              </button>
            ) : (
              <button
                type="button"
                onClick={stop}
                aria-label={t('Stop')}
                title={t('Stop')}
                className={cx(
                  'flex items-center justify-center size-8 rounded-full shrink-0 bg-secondary-200 hover:bg-accent-melon',
                  'transition-colors'
                )}
              >
                <div className="size-1/3 bg-white/80" />
              </button>
            )}
          </form>
        )}
      </AskAiShell>
    </motion.div>
  )
}
